import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { timeEntryService, customerService } from '../../services/api';
import {
  FormContainer,
  FormSection,
  FormGroup,
  Label,
  Input,
  Textarea,
  Select,
  FormButton,
  FormActions,
  ErrorMessage,
  FormAlert,
  HelpText
} from '../../components/common/FormElements';
import Card from '../../components/common/Card';
import { formatHoursMinutes } from '../../utils/formatHours';

// Styled components for this page
const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.dark};
  margin: 0;
`;

const BackButton = styled(FormButton)`
  margin-right: ${({ theme }) => theme.spacing[2]};
`;

const DeleteButton = styled(FormButton)`
  background-color: ${({ theme }) => theme.colors.error};
  &:hover {
    background-color: var(--act-danger);
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.dark};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: 9999px;

  ${({ status, theme }) => {
    if (status === 'approved') {
      return `
        background-color: var(--status-approved-plate);
        color: ${theme.colors.success};
      `;
    } else if (status === 'challenged') {
      return `
        background-color: rgba(195,37,29,.25);
        color: ${theme.colors.error};
      `;
    }
    return `
      background-color: var(--status-warning-plate);
      color: ${theme.colors.warning};
    `;
  }}
`;

// Modal components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: ${({ theme }) => theme.spacing[6]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ModalTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0;
`;

const ModalBody = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

// Validation schema (project is optional)
const buildTimeEntrySchema = (t) => Yup.object().shape({
  customerId: Yup.string().required(t('employee.entry.validation.customerRequired')),
  projectId: Yup.string(),
  date: Yup.date().required(t('employee.entry.validation.dateRequired')),
  hours: Yup.number()
    .required(t('employee.entry.validation.hoursRequired'))
    .min(0.1, t('employee.entry.validation.hoursMin'))
    .max(24, t('employee.entry.validation.hoursMax')),
  description: Yup.string()
    .required(t('employee.entry.validation.descriptionRequired'))
    .min(3, t('employee.entry.validation.descriptionMin')),
});

const TimeEntryDetail = () => {
  const { t } = useTranslation();
  const timeEntrySchema = useMemo(() => buildTimeEntrySchema(t), [t]);
  const { id } = useParams();
  const navigate = useNavigate();
  const [timeEntry, setTimeEntry] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const status = timeEntry?.status || 'pending';
  const isApproved = status === 'approved';

  const fetchProjects = async (customerId) => {
    if (!customerId) {
      setProjects([]);
      return;
    }
    try {
      const projectsData = await customerService.getCustomerProjects(customerId);
      setProjects(Array.isArray(projectsData) ? projectsData : []);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setProjects([]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch data in parallel for better performance
        const [timeEntryData, customersData] = await Promise.all([
          timeEntryService.getEmployeeTimeEntryById(id),
          customerService.getEmployeeCustomers()
        ]);

        setTimeEntry(timeEntryData);
        setCustomers(Array.isArray(customersData) ? customersData : []);

        // Load projects for the entry's customer
        if (timeEntryData?.customerId) {
          await fetchProjects(timeEntryData.customerId);
        }
      } catch (err) {
        console.error(err);
        setError(t('employee.entry.loadError'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, t]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSuccess(null);
      setError(null);

      // Customer and date identify the entry (unique per employee+customer+day)
      // and cannot be changed on an existing entry — delete and re-create instead.
      const payload = {
        projectId: values.projectId ? Number(values.projectId) : null,
        hours: parseFloat(values.hours),
        description: values.description,
      };

      await timeEntryService.updateTimeEntry(id, payload);
      setSuccess(t('employee.entry.updateSuccess'));

      // Refresh data
      const timeEntryData = await timeEntryService.getEmployeeTimeEntryById(id);
      setTimeEntry(timeEntryData);
    } catch (err) {
      console.error(err);
      setError(err.message || t('employee.entry.updateError'));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = () => {
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await timeEntryService.deleteTimeEntry(id);
      setShowDeleteModal(false);
      // Navigate back to the list view after successful deletion
      navigate('/employee/time-entries');
    } catch (err) {
      console.error(err);
      setError(err.message || t('employee.entry.deleteError'));
      setShowDeleteModal(false);
      setLoading(false);
    }
  };

  if (loading && !timeEntry) {
    return <div>{t('employee.entry.loadingDetail')}</div>;
  }

  if (error && !timeEntry) {
    return (
      <>
        <FormAlert variant="error">{error}</FormAlert>
        <FormButton onClick={() => navigate('/employee/time-entries')}>
          {t('employee.entry.backToEntries')}
        </FormButton>
      </>
    );
  }

  return (
    <>
      <PageHeader>
        <Title>{t('employee.entry.detailTitle')}</Title>
        <BackButton
          onClick={() => navigate('/employee/time-entries')}
          variant="outlined"
        >
          {t('employee.entry.backToList')}
        </BackButton>
      </PageHeader>

      <Card>
        {error && <FormAlert variant="error">{error}</FormAlert>}
        {success && <FormAlert variant="success">{success}</FormAlert>}

        {timeEntry && (
          <>
            <StatusRow>
              <span>{t('common.status')}:</span>
              <StatusBadge status={status}>{t(`status.${status}`)}</StatusBadge>
            </StatusRow>

            {status === 'challenged' && timeEntry.challengeReason && (
              <FormAlert variant="error">
                {t('employee.entry.challengeReason', { reason: timeEntry.challengeReason })}
              </FormAlert>
            )}

            {isApproved && (
              <FormAlert variant="info">
                {t('employee.entry.approvedLocked')}
              </FormAlert>
            )}

            <Formik
              initialValues={{
                customerId: timeEntry.customerId || '',
                projectId: timeEntry.projectId || '',
                date: timeEntry.date || '',
                hours: timeEntry.hours || 0,
                description: timeEntry.description || '',
              }}
              validationSchema={timeEntrySchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ isSubmitting, values, handleChange, handleBlur }) => (
                <Form>
                  <FormContainer>
                    <FormSection>
                      <FormGroup>
                        <Label htmlFor="customerId">{t('common.customer')}</Label>
                        {/* Customer identifies the entry and cannot change after creation */}
                        <Select
                          id="customerId"
                          name="customerId"
                          value={values.customerId}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled
                        >
                          <option value="">{t('employee.entry.selectCustomer')}</option>
                          {customers.map(customer => (
                            <option key={customer.id} value={customer.id}>
                              {customer.name}
                            </option>
                          ))}
                        </Select>
                        <ErrorMessage name="customerId" component="div" />
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="projectId">{t('common.project')}</Label>
                        <Select
                          id="projectId"
                          name="projectId"
                          value={values.projectId}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={isSubmitting || isApproved || !values.customerId}
                        >
                          <option value="">{t('employee.entry.selectProjectOptional')}</option>
                          {projects.map(project => (
                            <option key={project.id} value={project.id}>
                              {project.name}
                            </option>
                          ))}
                        </Select>
                        <ErrorMessage name="projectId" component="div" />
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="date">{t('common.date')}</Label>
                        {/* Date identifies the entry and cannot change after creation */}
                        <Input
                          type="date"
                          id="date"
                          name="date"
                          value={values.date}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled
                        />
                        <ErrorMessage name="date" component="div" />
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="hours">{t('common.hours')}</Label>
                        <Input
                          type="number"
                          id="hours"
                          name="hours"
                          step="0.1"
                          min="0.1"
                          max="24"
                          value={values.hours}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={isSubmitting || isApproved}
                        />
                        {/* The field itself stays decimal — that is what the API takes —
                            but the employee gets the H:MM reading of the entry. */}
                        {values.hours !== '' && <HelpText>= {formatHoursMinutes(values.hours)}</HelpText>}
                        <ErrorMessage name="hours" component="div" />
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="description">{t('common.description')}</Label>
                        <Textarea
                          id="description"
                          name="description"
                          rows="4"
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={isSubmitting || isApproved}
                        />
                        <ErrorMessage name="description" component="div" />
                      </FormGroup>
                    </FormSection>

                    {!isApproved && (
                      <FormActions>
                        <DeleteButton
                          type="button"
                          onClick={confirmDelete}
                          disabled={isSubmitting}
                        >
                          {t('common.delete')}
                        </DeleteButton>
                        <FormButton
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? t('common.saving') : t('employee.entry.saveChanges')}
                        </FormButton>
                      </FormActions>
                    )}
                  </FormContainer>
                </Form>
              )}
            </Formik>
          </>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{t('employee.entry.confirmDeletionTitle')}</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <p>{t('employee.entry.confirmDeletionBody')}</p>
            </ModalBody>
            <ModalFooter>
              <BackButton onClick={cancelDelete} variant="outlined">
                {t('common.cancel')}
              </BackButton>
              <DeleteButton onClick={handleDelete}>
                {t('common.delete')}
              </DeleteButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default TimeEntryDetail;
